import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiClock, FiHeart, FiShoppingCart, FiStar } from "react-icons/fi";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useAuth } from "../../context/AuthContext";
import { userAPI } from "../../services/api";
import { toast } from "react-toastify";
import { getDealImageUrl } from "../../utils/dealImageHelper";

const FALLBACK_IMG = "/placeholder.jpg";

const clamp = (v, max = 90) => {
  const s = String(v || "").trim();
  if (!s) return "";
  return s.length > max ? s.slice(0, max) + "..." : s;
};

const getRatingValue = (deal) => {
  if (typeof deal?.rating === "number") return deal.rating;
  if (typeof deal?.stars === "number") return deal.stars;
  if (typeof deal?.averageRating === "number") return deal.averageRating;
  if (typeof deal?.reviewsAvg === "number") return deal.reviewsAvg;

  if (Array.isArray(deal?.reviews) && deal.reviews.length) {
    const nums = deal.reviews
      .map((r) => Number(r?.rating))
      .filter((n) => !Number.isNaN(n));
    if (nums.length) return nums.reduce((a, b) => a + b, 0) / nums.length;
  }
  return 0;
};

const getReviewsCount = (deal) => {
  if (typeof deal?.reviewCount === "number") return deal.reviewCount;
  if (typeof deal?.reviewsCount === "number") return deal.reviewsCount;
  if (typeof deal?.numReviews === "number") return deal.numReviews;
  if (typeof deal?.ratingsCount === "number") return deal.ratingsCount;
  if (Array.isArray(deal?.reviews)) return deal.reviews.length;
  return 0;
};

const getBoughtCount = (deal) => {
  if (typeof deal?.quantitySold === "number") return deal.quantitySold;

  const candidates = [
    deal?.boughtCount,
    deal?.ordersCount,
    deal?.salesCount,
    deal?.sold,
    deal?.soldCount,
    deal?.purchases,
    deal?.purchasesCount,
    deal?.totalOrders
  ];

  for (const v of candidates) {
    const n = Number(v);
    if (!Number.isNaN(n) && n >= 0) return n;
  }

  const n2 = Number(deal?.stats?.sold ?? deal?.stats?.orders);
  if (!Number.isNaN(n2) && n2 >= 0) return n2;
  return 0;
};

const renderStars = (rating) => {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  const full = Math.round(r);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <FiStar
          key={s}
          size={14}
          className={s <= full ? "text-yellow-400 fill-current" : "text-gray-300"}
        />
      ))}
    </div>
  );
};

const DealCard = ({
  deal,
  showAction = false,
  actionLabel = "Voir l'offre",
  actionClassName = "bg-[#e11d7b] hover:bg-[#c91469]"
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);

  const endDateRaw = deal?.endDate || deal?.endDateISO || deal?.validUntil || deal?.end || null;

  let daysRemaining = null;
  let isExpired = false;

  if (endDateRaw) {
    const diffMs = new Date(endDateRaw).getTime() - Date.now();
    const raw = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    daysRemaining = Number.isFinite(raw) ? Math.max(0, raw) : 0;
    isExpired = daysRemaining <= 0;
  } else {
    const status = (deal?.status || "").toLowerCase();
    if (["expired", "expiré", "inactive", "archived", "finished", "ended"].includes(status)) {
      isExpired = true;
      daysRemaining = 0;
    } else {
      isExpired = false;
      daysRemaining = null;
    }
  }

  const quantityRemaining = Number(
    deal?.quantityRemaining ?? deal?.remaining ?? deal?.stock ?? deal?.quantity ?? 0
  );

  const discountPercentage =
    deal?.discountPercentage ??
    ((Number(deal?.originalPrice) || 0) > 0
      ? Math.round(
          ((Number(deal?.originalPrice) - (Number(deal?.discountedPrice) || 0)) /
            Number(deal?.originalPrice)) *
            100
        )
      : 0);

  const ratingValue = useMemo(() => getRatingValue(deal), [deal]);
  const ratingCount = useMemo(() => getReviewsCount(deal), [deal]);
  const boughtCount = useMemo(() => getBoughtCount(deal), [deal]);

  const descriptionText = useMemo(() => {
    return deal?.shortDescription || deal?.description || deal?.summary || "";
  }, [deal]);

  // ✅ Obtenir l'URL de l'image - getDealImageUrl gère tous les cas
  const imgSrc = useMemo(() => getDealImageUrl(deal, 0, FALLBACK_IMG), [deal]);

  useEffect(() => {
    if (isExpired) {
      setIsFavorite(false);
      return;
    }

    let mounted = true;

    const checkFavorite = async () => {
      if (!user) {
        if (mounted) setIsFavorite(false);
        return;
      }
      try {
        const res = await userAPI.getFavorites();
        const favs = res?.data?.data || res?.data || [];
        const isFav =
          Array.isArray(favs) &&
          favs.some(
            (fav) =>
              (fav?._id || fav?.id)?.toString() === (deal?._id || deal?.id)?.toString()
          );
        if (mounted) setIsFavorite(Boolean(isFav));
      } catch {
        if (mounted) setIsFavorite(false);
      }
    };

    checkFavorite();
    return () => {
      mounted = false;
    };
  }, [user, deal?._id, deal?.id, isExpired]);

  const handleFavoriteClick = async (e) => {
    if (isExpired) return;
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error("Connectez-vous pour ajouter aux favoris");
      navigate("/login");
      return;
    }

    const dealId = deal?._id || deal?.id;
    if (!dealId) return;

    try {
      if (isFavorite) {
        await userAPI.removeFavorite(dealId);
        setIsFavorite(false);
        toast.success("Retiré des favoris");
      } else {
        await userAPI.addFavorite(dealId);
        setIsFavorite(true);
        toast.success("Ajouté aux favoris");
      }
    } catch {
      toast.error("Erreur lors de la modification des favoris");
    }
  };

  const href = `/deals/${deal?.slug || deal?._id || deal?.id}`;

  const handleCardClick = (e) => {
    if (isExpired) return;
    if (e.target.closest("button") || e.target.closest("a")) return;
    navigate(href);
  };

  const handleActionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(href);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group block h-full cursor-pointer ${isExpired ? "pointer-events-none opacity-60" : ""}`}
      aria-label={deal?.title}
    >
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
        <div className="relative w-full h-56 bg-gray-100 overflow-hidden">
          <img
            src={imgSrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-50"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMG;
            }}
          />


  <LazyLoadImage
    src={imgSrc}
    alt={deal?.title || "Deal"}
    effect="blur"
    onError={(e) => {
      e.currentTarget.src = FALLBACK_IMG;
    }}
    className="w-full h-full object-cover"
    wrapperClassName="w-full h-full"
  />

  {isExpired && (
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
      <span className="bg-red-600 text-white font-bold px-4 py-2 text-lg rounded">
        Expiré
      </span>
    </div>
  )}

  {!isExpired && discountPercentage > 0 && (
    <div className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full font-bold text-sm z-20">
      -{discountPercentage}%
    </div>
  )}

  {!isExpired && (
    <button
      type="button"
      onClick={handleFavoriteClick}
      aria-pressed={isFavorite}
      className="absolute top-2 left-2 bg-white p-2 rounded-full hover:bg-gray-100 transition-colors shadow-sm z-20"
    >
      <FiHeart
        size={18}
        className={isFavorite ? "text-red-500 fill-current" : "text-gray-600"}
      />
    </button>
  )}
</div>


        <div className="p-3.5 flex flex-col flex-1">
          <h3 className="text-base font-semibold text-gray-900 mb-1.5 line-clamp-2 h-12">
            {deal?.title}
          </h3>

          <p className="text-sm text-gray-600 mb-2.5 line-clamp-2 h-10">
            {clamp(descriptionText, 95) || "—"}
          </p>

          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-end gap-2">
              <span className="text-xl font-bold text-[#e11d7b]">
                {deal?.discountedPrice ?? ""} MAD
              </span>
              {deal?.originalPrice != null && (
                <span className="text-sm text-gray-500 line-through">
                  {deal.originalPrice} MAD
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500 mb-2.5">
            <div className="flex items-center gap-2">
              {renderStars(ratingValue)}
              <span>({ratingCount || 0})</span>
            </div>

            <div className="flex items-center gap-1">
              <FiShoppingCart size={14} />
              <span>{boughtCount || 0} achetés</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
            {isExpired ? (
              <span className="text-red-600 font-semibold">Expiré</span>
            ) : daysRemaining != null ? (
              <span className="flex items-center gap-1">
                <FiClock size={14} />
                <span>
                  {daysRemaining} {daysRemaining <= 1 ? "jour" : "jours"}
                </span>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <FiClock size={14} />
                <span>En cours</span>
              </span>
            )}

            <span className="text-gray-600">
              {quantityRemaining > 0 ? `${quantityRemaining} dispo` : "Stock limité"}
            </span>
          </div>

          {showAction && !isExpired && (
            <button
              type="button"
              onClick={handleActionClick}
              className={`w-full text-center text-white font-semibold py-2 rounded-lg transition ${actionClassName}`}
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealCard;